export interface EncryptedFileMetadata {
  fileKey: string;
  iv: string;
  originalName: string;
  mime: string;
  size: number;
}

export interface EncryptedFileResult {
  encryptedBytes: ArrayBuffer;
  metadata: EncryptedFileMetadata;
}

function ab2b64(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

function b642ab(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export async function encryptFile(
  file: File,
): Promise<EncryptedFileResult> {
  const fileKey = crypto.getRandomValues(new Uint8Array(32));
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const key = await crypto.subtle.importKey(
    "raw",
    fileKey,
    { name: "AES-GCM" },
    false,
    ["encrypt"],
  );

  const fileBytes = await file.arrayBuffer();

  const encryptedBytes = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    fileBytes,
  );

  return {
    encryptedBytes,
    metadata: {
      fileKey: ab2b64(fileKey.buffer),
      iv: ab2b64(iv.buffer),
      originalName: file.name,
      mime: file.type || "application/octet-stream",
      size: file.size,
    },
  };
}

export async function decryptFile(
  encryptedBytes: ArrayBuffer,
  metadata: EncryptedFileMetadata,
): Promise<Blob> {
  const fileKey = b642ab(metadata.fileKey);
  const iv = b642ab(metadata.iv);

  const key = await crypto.subtle.importKey(
    "raw",
    fileKey,
    { name: "AES-GCM" },
    false,
    ["decrypt"],
  );

  const decryptedBytes = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    encryptedBytes,
  );

  return new Blob([decryptedBytes], { type: metadata.mime });
}

export function downloadDecryptedFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function createEncryptedFileBlob(
  encryptedBytes: ArrayBuffer,
): Blob {
  return new Blob([encryptedBytes], { type: "application/octet-stream" });
}
